/**
 * Eastern-Time (America/New_York) formatting helpers (Feb 2026).
 *
 * Two problems this file solves:
 *
 * 1. Server timestamp shape — MongoDB reads back datetimes as *naive*
 *    (no tzinfo), so many of our backend serializers emit strings like
 *    `2026-07-07T23:12:11.930000` with no `Z` or `+00:00`. Browsers
 *    interpret naive ISO strings as **local time**, which on an ET user
 *    lands the display 4 h ahead of the true UTC moment.
 *
 *    `parseServerDate()` normalises this by appending `Z` when no
 *    timezone offset is present so `new Date(...)` treats the input as
 *    UTC.
 *
 * 2. Ash & Merch is a US East-Coast delivery operation — every operator,
 *    customer and driver is on Eastern Time regardless of where their
 *    laptop clock is. All display helpers force `timeZone:
 *    'America/New_York'` so the UI stays consistent everywhere.
 *
 * Use these in any component that shows a server timestamp:
 *   parseServerDate, formatTimeET, formatDateET, formatDateTimeET,
 *   dayLabelET, relativeTimeET
 */

const ZONE = 'America/New_York';

/**
 * Parse an ISO 8601 string that may be missing a timezone offset. Naive
 * strings are treated as UTC (this is what MongoDB actually stored).
 */
export function parseServerDate(iso) {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  let s = String(iso);
  // Already has a timezone offset (Z, +hh:mm or -hh:mm at the end)?
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasTz) s += 'Z';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatTimeET(iso) {
  const d = parseServerDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', {
    timeZone: ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDateET(iso, { withYear = 'auto' } = {}) {
  const d = parseServerDate(iso);
  if (!d) return '';
  const opts = { timeZone: ZONE, month: 'short', day: 'numeric' };
  const nowYear = new Date().getFullYear();
  const dYear = Number(d.toLocaleString('en-US', { timeZone: ZONE, year: 'numeric' }));
  if (withYear === true || (withYear === 'auto' && dYear !== nowYear)) {
    opts.year = 'numeric';
  }
  return d.toLocaleDateString('en-US', opts);
}

export function formatDateTimeET(iso) {
  const d = parseServerDate(iso);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    timeZone: ZONE,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Same-day comparison using the ET calendar, not the browser's calendar. */
function etDateParts(d) {
  const s = d.toLocaleString('en-US', {
    timeZone: ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-US locale short-date is `MM/DD/YYYY`
  const [mm, dd, yyyy] = s.split(/[/,\s]+/);
  return `${yyyy}-${mm}-${dd}`;
}

function etTodayKey() {
  return etDateParts(new Date());
}

function etYesterdayKey() {
  const y = new Date();
  y.setUTCDate(y.getUTCDate() - 1);
  return etDateParts(y);
}

export function dayLabelET(iso) {
  const d = parseServerDate(iso);
  if (!d) return '';
  const k = etDateParts(d);
  if (k === etTodayKey()) return 'Today';
  if (k === etYesterdayKey()) return 'Yesterday';
  // Older: show short date in ET (year only if different)
  return formatDateET(iso, { withYear: 'auto' });
}

/** "just now" / "5m ago" / "3h ago" / "Feb 12" — for feed rows. */
export function relativeTimeET(iso) {
  const d = parseServerDate(iso);
  if (!d) return '';
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 45) return 'just now';
  if (diffSec < 60 * 60) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 60 * 60 * 24) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 60 * 60 * 24 * 6) return `${Math.floor(diffSec / 86400)}d ago`;
  return formatDateET(iso);
}
